import React, { useState, useEffect } from 'react';
import { tierDisplayName } from '../utils/tierNames';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import NotificationBell from '../components/NotificationBell';
import AppShell from '../components/AppShell';
import { SharesPanel } from '../components/SharesPanel';
import { SocialAccountsPanel } from '../components/SocialAccountsPanel';
import {
  Shield, AlertTriangle, Copy, Check,
  User, Mail, Crown, Calendar, Globe, Bell, BellOff, Lock,
  Download, Trash2, ExternalLink, Sparkles, Zap, Loader2, Settings, CreditCard, MessageCircle,
  Eye, EyeOff, Share2, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section Card Component
const SettingsSection = ({ icon: Icon, title, description, children, iconColor = 'text-primary', className = '' }) => (
  <div className={`bg-zinc-900/50 border border-zinc-800/50 rounded-2xl p-6 card-lift ${className}`} data-testid={`section-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start gap-4 mb-6">
      <div className={`w-12 h-12 rounded-xl bg-zinc-800/50 flex items-center justify-center flex-shrink-0 ${iconColor}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {description && <p className="text-sm text-zinc-500 mt-1">{description}</p>}
      </div>
    </div>
    {children}
  </div>
);

// Info Row Component
const InfoRow = ({ label, value, badge }) => (
  <div className="flex items-center justify-between py-3 border-b border-zinc-800/50 last:border-0">
    <span className="text-sm text-zinc-400">{label}</span>
    <div className="flex items-center gap-2">
      <span className="font-medium text-white">{value}</span>
      {badge}
    </div>
  </div>
);

// Toggle Row Component
const ToggleRow = ({ icon: Icon, title, description, checked, onChange, disabled }) => (
  <div className="flex items-center justify-between py-4 border-b border-zinc-800/50 last:border-0">
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="w-9 h-9 rounded-lg bg-zinc-800/50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-zinc-400" />
        </div>
      )}
      <div>
        <p className="font-medium text-white text-sm">{title}</p>
        {description && <p className="text-xs text-zinc-500 mt-0.5">{description}</p>}
      </div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
  </div>
);

const SettingsPage = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // NSFW Dialog State
  const [nsfwDialogOpen, setNsfwDialogOpen] = useState(false);
  const [nsfwLoading, setNsfwLoading] = useState(false);
  const [ageVerified, setAgeVerified] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  
  // Preference State
  const [language, setLanguage] = useState('en');
  const [contentLanguage, setContentLanguage] = useState('auto');
  const [notifications, setNotifications] = useState({
    email: true,
    marketing: false,
    tips: true
  });
  
  // Copy state
  const [copied, setCopied] = useState(false);
  
  // Delete account confirmation
  const [deleteConfirm, setDeleteConfirm] = useState('');
  
  // Demo mode state
  const [demoMode, setDemoMode] = useState(user?.demo_mode || false);
  const [demoToggling, setDemoToggling] = useState(false);

  const handleToggleDemoMode = async () => {
    setDemoToggling(true);
    try {
      const resp = await axios.put(`${API}/settings/demo-mode`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDemoMode(resp.data.demo_mode);
      await refreshUser();
      toast.success(resp.data.demo_mode ? 'Demo Mode enabled' : 'Demo Mode disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle demo mode');
    } finally {
      setDemoToggling(false);
    }
  };

  const copyAffiliateCode = () => {
    if (user?.affiliate_code) {
      navigator.clipboard.writeText(user.affiliate_code);
      setCopied(true);
      toast.success('Affiliate code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleEnableNSFW = async () => {
    if (!ageVerified || !consentAccepted) {
      toast.error('You must verify your age and accept the consent');
      return;
    }

    setNsfwLoading(true);
    try {
      await axios.post(
        `${API}/nsfw/enable`,
        { age_verified: ageVerified, consent_accepted: consentAccepted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('NSFW Mode enabled');
      refreshUser();
      setNsfwDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable NSFW mode');
    } finally {
      setNsfwLoading(false);
    }
  };

  const handleDisableNSFW = async () => {
    try {
      await axios.post(`${API}/nsfw/disable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('NSFW Mode disabled');
      refreshUser();
    } catch (error) {
      toast.error('Failed to disable NSFW mode');
    }
  };

  const handleExportData = async () => {
    toast.info('Preparing your data export...');
    try {
      const response = await axios.get(`${API}/user/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'delphi-ai-data-export.json';
      a.click();
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Data export is not available yet');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    toast.error('Account deletion requires contacting support');
  };

  const tierAllowsNSFW = ['pro', 'elite', 'agency'].includes(user?.tier) || user?.is_founder;

  // Format renewal date
  const getRenewalDate = () => {
    if (user?.subscription_end) {
      return new Date(user.subscription_end).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric'
      });
    }
    return 'N/A';
  };

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10 max-w-3xl mx-auto">
          
          {/* Header */}
          <div className="mb-10 animate-fadeInUp">
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">System Config</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              Settings
            </h1>
            <p className="text-zinc-500 text-sm mt-2">Manage your account and preferences</p>
          </div>

          <div className="space-y-6">
            
            {/* ═══════════════════════════════════════════════════════════════════════
                SECTION 1: Account Overview
            ═══════════════════════════════════════════════════════════════════════ */}
            <SettingsSection
              icon={User}
              title="Account Overview"
              description="Your account details and subscription"
              className="animate-fadeInUp delay-75"
            >
              <div className="space-y-0">
                <InfoRow 
                  label="Name" 
                  value={user?.name || '—'} 
                />
                <InfoRow 
                  label="Email" 
                  value={user?.email || '—'} 
                />
                <InfoRow 
                  label="Subscription" 
                  value={tierDisplayName(user?.tier)}
                  badge={
                    user?.is_founder && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400">
                        <Crown className="w-3 h-3" />
                        Founder
                      </span>
                    )
                  }
                />
                <InfoRow 
                  label="Billing Cycle" 
                  value={user?.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}
                />
                <InfoRow 
                  label="Renewal Date" 
                  value={getRenewalDate()}
                />
              </div>
              
              <div className="mt-6">
                <Link to="/billing">
                  <Button variant="outline" className="w-full border-zinc-700 hover:bg-zinc-800">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Manage Subscription
                  </Button>
                </Link>
              </div>
            </SettingsSection>

            {/* ═══════════════════════════════════════════════════════════════════════
                SECTION 2: Preferences
            ═══════════════════════════════════════════════════════════════════════ */}
            <SettingsSection
              icon={Globe}
              title="Preferences"
              description="Language and notification settings"
              className="animate-fadeInUp delay-100"
            >
              {/* Language Settings */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white text-sm">Interface Language</p>
                    <p className="text-xs text-zinc-500">Language for menus and buttons</p>
                  </div>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className="w-32 bg-zinc-800/50 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                      <SelectItem value="pt">Portuguese</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white text-sm">Content Language</p>
                    <p className="text-xs text-zinc-500">Default language for generated content</p>
                  </div>
                  <Select value={contentLanguage} onValueChange={setContentLanguage}>
                    <SelectTrigger className="w-32 bg-zinc-800/50 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="auto">Auto-detect</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                      <SelectItem value="pt">Portuguese</SelectItem>
                      <SelectItem value="zh">Chinese</SelectItem>
                      <SelectItem value="ja">Japanese</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Notification Toggles */}
              <div className="border-t border-zinc-800/50 pt-4">
                <p className="text-sm font-medium text-zinc-400 mb-3">Notifications</p>
                <ToggleRow
                  icon={Mail}
                  title="Email Notifications"
                  description="Important updates about your account"
                  checked={notifications.email}
                  onChange={(v) => setNotifications({...notifications, email: v})}
                />
                <ToggleRow
                  icon={Bell}
                  title="Tips & Tutorials"
                  description="Learn how to create better content"
                  checked={notifications.tips}
                  onChange={(v) => setNotifications({...notifications, tips: v})}
                />
                <ToggleRow
                  icon={BellOff}
                  title="Marketing Emails"
                  description="New features and special offers"
                  checked={notifications.marketing}
                  onChange={(v) => setNotifications({...notifications, marketing: v})}
                />
              </div>
            </SettingsSection>

            {/* ═══════════════════════════════════════════════════════════════════════
                SECTION 3: NSFW Mode
            ═══════════════════════════════════════════════════════════════════════ */}
            <SettingsSection
              icon={Shield}
              title="NSFW Mode"
              description="Explicit adult content generation"
              iconColor="text-yellow-500"
              className="animate-fadeInUp delay-150"
            >
              {!tierAllowsNSFW ? (
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-zinc-500" />
                    <div>
                      <p className="text-sm font-medium text-zinc-300">Pro tier or higher required</p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        <Link to="/billing" className="text-primary hover:underline">Upgrade your plan</Link> to unlock NSFW content generation
                      </p>
                    </div>
                  </div>
                </div>
              ) : user?.nsfw_enabled ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-yellow-400" />
                      </div>
                      <div>
                        <p className="font-medium text-yellow-400">NSFW Mode is Active</p>
                        <p className="text-xs text-zinc-400 mt-0.5">Explicit content generation enabled</p>
                      </div>
                    </div>
                    <Switch
                      checked={true}
                      onCheckedChange={handleDisableNSFW}
                      data-testid="nsfw-toggle"
                    />
                  </div>
                  
                  <div className="text-xs text-zinc-500 space-y-1">
                    <p>Age verified: Yes</p>
                    <p>Consent accepted: Yes</p>
                    <p className="text-yellow-500/70">Content is subject to community guidelines</p>
                  </div>
                  
                  {/* View Rules Link */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 border-zinc-700 hover:bg-zinc-800 text-xs"
                    onClick={() => toast.info('NSFW guidelines available in Help section')}
                  >
                    View NSFW Guidelines
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-zinc-400">
                    Enable NSFW mode to generate explicit adult content. 
                    Requires age verification (18+) and consent to terms.
                  </p>
                  
                  {/* Founder Quick Setup */}
                  {user?.is_founder && (
                    <Button
                      onClick={async () => {
                        try {
                          setNsfwLoading(true);
                          const response = await axios.post(
                            `${API}/nsfw/founder-setup`,
                            {},
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success('NSFW mode enabled for testing');
                          refreshUser();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Setup failed');
                        } finally {
                          setNsfwLoading(false);
                        }
                      }}
                      disabled={nsfwLoading}
                      className="w-full bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30"
                      data-testid="founder-nsfw-setup-btn"
                    >
                      {nsfwLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      Founder: Quick NSFW Setup
                    </Button>
                  )}
                  
                  <Button
                    onClick={() => setNsfwDialogOpen(true)}
                    className="w-full bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30"
                    data-testid="enable-nsfw-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Enable NSFW Mode
                  </Button>
                </div>
              )}
            </SettingsSection>

            {/* ═══════════════════════════════════════════════════════════════════════
                SECTION 4: Affiliate Code
            ═══════════════════════════════════════════════════════════════════════ */}
            {user?.affiliate_code && (
              <SettingsSection
                icon={Gift}
                title="Affiliate Program"
                description="Earn 25% recurring commission"
                iconColor="text-emerald-400"
                className="animate-fadeInUp delay-200"
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Input
                      value={user.affiliate_code}
                      readOnly
                      className="bg-zinc-800/50 border-zinc-700 font-mono"
                    />
                    <Button
                      onClick={copyAffiliateCode}
                      variant="outline"
                      className="border-zinc-700 hover:bg-zinc-800 btn-press"
                      data-testid="copy-affiliate-btn"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <Link to="/affiliates">
                    <Button variant="outline" className="w-full border-zinc-700 hover:bg-zinc-800 btn-press">
                      View Affiliate Dashboard
                    </Button>
                  </Link>
                </div>
              </SettingsSection>
            )}

            {/* ═══════════════════════════════════════════════════════════════════════
                SECTION: Founder Tools (Demo Mode)
            ═══════════════════════════════════════════════════════════════════════ */}
            {user?.is_founder && (
              <SettingsSection
                icon={Crown}
                title="Founder Tools"
                description="Tools for demos and outreach"
                iconColor="text-amber-400"
                className="animate-fadeInUp delay-200"
              >
                <div className="space-y-4">
                  <ToggleRow
                    icon={demoMode ? Eye : EyeOff}
                    title="Enable Demo Mode"
                    description="Hide your assets, show curated demo campaigns, and lock editing. Perfect for live demos."
                    checked={demoMode}
                    onChange={handleToggleDemoMode}
                    disabled={demoToggling}
                  />
                  {demoMode && (
                    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="demo-mode-active-banner">
                      <p className="text-xs text-amber-400 flex items-center gap-2">
                        <Eye className="w-3.5 h-3.5" />
                        Demo Mode is active — your personal assets are hidden, curated campaigns are shown, and destructive actions are disabled.
                      </p>
                    </div>
                  )}
                </div>
              </SettingsSection>
            )}

            {/* ═══════════════════════════════════════════════════════════════════════
                SECTION: Social Accounts
            ═══════════════════════════════════════════════════════════════════════ */}
            <SettingsSection
              icon={Link2}
              title="Social Accounts"
              description="Connect the channels where Delphi will publish for you"
              iconColor="text-cyan-400"
              className="animate-fadeInUp delay-200"
            >
              <SocialAccountsPanel token={token} isFounder={user?.is_founder} />
            </SettingsSection>

            {/* ═══════════════════════════════════════════════════════════════════════
                SECTION: Public Share Links
            ═══════════════════════════════════════════════════════════════════════ */}
            <SettingsSection
              icon={Share2}
              title="Public Share Links"
              description="Track views and manage your shared creations"
              iconColor="text-orange-400"
              className="animate-fadeInUp delay-200"
            >
              <SharesPanel token={token} />
            </SettingsSection>

            {/* ═══════════════════════════════════════════════════════════════════════
                SECTION 5: Privacy & Data
            ═══════════════════════════════════════════════════════════════════════ */}
            <SettingsSection
              icon={Lock}
              title="Privacy & Data"
              description="Your data and privacy controls"
              className="animate-fadeInUp delay-200"
            >
              <div className="space-y-4">
                {/* Export Data */}
                <div className="flex items-center justify-between p-4 bg-zinc-800/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Download className="w-5 h-5 text-zinc-400" />
                    <div>
                      <p className="font-medium text-white text-sm">Export My Data</p>
                      <p className="text-xs text-zinc-500">Download all your account data</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleExportData}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700 hover:bg-zinc-800"
                  >
                    Export
                  </Button>
                </div>

                {/* Privacy Policy */}
                <div className="flex items-center justify-between p-4 bg-zinc-800/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <ExternalLink className="w-5 h-5 text-zinc-400" />
                    <div>
                      <p className="font-medium text-white text-sm">Privacy Policy</p>
                      <p className="text-xs text-zinc-500">How we handle your data</p>
                    </div>
                  </div>
                  <a href="/privacy" target="_blank">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-zinc-700 hover:bg-zinc-800"
                    >
                      View
                    </Button>
                  </a>
                </div>

                {/* Delete Account */}
                <div className="border-t border-zinc-800/50 pt-4 mt-6">
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <Trash2 className="w-5 h-5 text-red-400" />
                      <div>
                        <p className="font-medium text-red-400 text-sm">Delete Account</p>
                        <p className="text-xs text-zinc-500">Permanently remove your account and all data</p>
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500 mb-3">
                      Type <span className="font-mono text-red-400">DELETE</span> to confirm:
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        placeholder="Type DELETE"
                        className="bg-zinc-800/50 border-zinc-700 flex-1"
                      />
                      <Button
                        onClick={handleDeleteAccount}
                        variant="destructive"
                        size="sm"
                        disabled={deleteConfirm !== 'DELETE'}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </SettingsSection>

            {/* ═══════════════════════════════════════════════════════════════════════
                SECTION 6: Support
            ═══════════════════════════════════════════════════════════════════════ */}
            <SettingsSection
              icon={MessageCircle}
              title="Support"
              description="Get help when you need it"
              className="animate-fadeInUp delay-250"
            >
              <div className="space-y-3">
                <a href="mailto:infodelphiai@gmail.com">
                  <Button variant="outline" className="w-full border-zinc-700 hover:bg-zinc-800 btn-press">
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Support
                  </Button>
                </a>
                <p className="text-xs text-zinc-500 text-center">
                  We typically respond within 24 hours
                </p>
              </div>
            </SettingsSection>

          </div>
        </div>

      {/* NSFW Enable Dialog */}
      {nsfwDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setNsfwDialogOpen(false)} />
          <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-md w-full" data-testid="nsfw-dialog">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-white">
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
              Enable NSFW Mode
            </h2>
            
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                By enabling NSFW mode, you agree to the following:
              </p>
              
              <ul className="text-sm space-y-2 text-zinc-400">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-500">•</span>
                  You are 18 years or older
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-500">•</span>
                  You accept the NSFW content addendum
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-500">•</span>
                  You will not create illegal content
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-500">•</span>
                  You will not create content involving minors
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-500">•</span>
                  You will not create non-consensual content
                </li>
              </ul>

              <div className="space-y-3 pt-4 border-t border-zinc-800">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ageVerified}
                    onChange={(e) => setAgeVerified(e.target.checked)}
                    className="w-5 h-5 rounded border-zinc-600 bg-zinc-800"
                    data-testid="age-verify-checkbox"
                  />
                  <span className="text-sm text-zinc-300">I confirm I am 18 years or older</span>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentAccepted}
                    onChange={(e) => setConsentAccepted(e.target.checked)}
                    className="w-5 h-5 rounded border-zinc-600 bg-zinc-800"
                    data-testid="consent-checkbox"
                  />
                  <span className="text-sm text-zinc-300">I accept the NSFW terms and content guidelines</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setNsfwDialogOpen(false)}
                  variant="outline"
                  className="flex-1 border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEnableNSFW}
                  disabled={!ageVerified || !consentAccepted || nsfwLoading}
                  className="flex-1 bg-yellow-500 text-black hover:bg-yellow-400"
                  data-testid="confirm-nsfw-btn"
                >
                  {nsfwLoading ? 'Enabling...' : 'Enable NSFW'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default SettingsPage;
